import {
	useRef,
	useState,
	useCallback,
	useEffect,
	forwardRef,
	useImperativeHandle,
	memo,
} from 'react';
import styled from 'styled-components';
import { LuPlay, LuPause, LuVolume2, LuVolumeOff } from 'react-icons/lu';
import VirtualLineCanvas from '@components/video/VirtualLineCanvas';

const Wrapper = styled.div`
	background: #0a0a0a;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid ${({ theme }) => theme.colors.border};
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

const VideoFrame = styled.div`
	flex: 1;
	position: relative;
	background: linear-gradient(135deg, #0d1a0d 0%, #050a05 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 0;
	overflow: hidden;

	video {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}
`;

const Placeholder = styled.div`
	font-size: 48px;
	opacity: 0.6;
`;

const SeekingOverlay = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.4);
	z-index: 2;
`;

const Spinner = styled.div`
	width: 32px;
	height: 32px;
	border: 3px solid rgba(255, 255, 255, 0.2);
	border-top-color: ${({ theme }) => theme.colors.green};
	border-radius: 50%;
	animation: spin 0.6s linear infinite;

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

const DetectionOverlay = styled.div`
	position: absolute;
	border: 2px solid #60a5fa;
	padding: 2px 5px;
	font-size: 10px;
	color: #60a5fa;
	background: rgba(96, 165, 250, 0.08);
	border-radius: 3px;
	pointer-events: none;
`;

const Controls = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 12px;
	background: rgba(0, 0, 0, 0.4);
	flex-shrink: 0;
`;

const PlayButton = styled.button`
	width: 30px;
	height: 30px;
	border-radius: 50%;
	background: ${({ theme }) => theme.colors.green};
	border: none;
	color: #fff;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	flex-shrink: 0;
`;

const Track = styled.div`
	flex: 1;
	height: 3px;
	background: ${({ theme }) => theme.colors.backgroundAccent};
	border-radius: 2px;
	position: relative;
	cursor: pointer;
`;

const TrackFill = styled.div`
	height: 100%;
	background: ${({ theme }) => theme.colors.green};
	border-radius: 2px;
	width: ${({ $pct }) => $pct}%;
`;

const TrackThumb = styled.div`
	position: absolute;
	top: -5px;
	left: ${({ $pct }) => $pct}%;
	width: 12px;
	height: 12px;
	background: #fff;
	border-radius: 50%;
	transform: translateX(-50%);
`;

const TimeLabel = styled.span`
	font-size: 11px;
	color: ${({ theme }) => theme.colors.textAccent};
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
`;

const VolumeButton = styled.span`
	font-size: 14px;
	cursor: pointer;
	opacity: 0.6;
	transition: opacity 0.2s;
	display: flex;
	align-items: center;
	&:hover {
		opacity: 1;
	}
`;

const formatTime = seconds => {
	if (!seconds || isNaN(seconds)) return '00:00';
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60);
	return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const VideoPlayer = memo(
	forwardRef(({ videoUrl, detections = [], onTimeUpdate, virtualLine }, ref) => {
		const videoRef = useRef(null);
		const trackRef = useRef(null);
		const [isPlaying, setIsPlaying] = useState(false);
		const [currentTime, setCurrentTime] = useState(0);
		const [duration, setDuration] = useState(0);
		const [isMuted, setIsMuted] = useState(false);
		const [isBuffering, setIsBuffering] = useState(false);
		const [videoElement, setVideoElement] = useState(null);

		// Callback ref: captures the video DOM element into state
		const videoCallbackRef = useCallback(node => {
			videoRef.current = node;
			setVideoElement(node);
		}, []);

		useImperativeHandle(
			ref,
			() => ({
				seekTo: seconds => {
					const vid = videoRef.current;
					if (vid) vid.currentTime = seconds;
				},
			}),
			[]
		);

		const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

		const togglePlay = useCallback(() => {
			const vid = videoRef.current;
			if (!vid) return;
			if (vid.paused) {
				vid.play()
					.then(() => setIsPlaying(true))
					.catch(err => console.warn('Playback failed:', err));
			} else {
				vid.pause();
				setIsPlaying(false);
			}
		}, []);

		const handleTimeUpdate = useCallback(() => {
			const vid = videoRef.current;
			if (!vid) return;
			setCurrentTime(vid.currentTime);
			onTimeUpdate?.(vid.currentTime);
		}, [onTimeUpdate]);

		const handleLoadedMetadata = useCallback(() => {
			const vid = videoRef.current;
			if (vid) setDuration(vid.duration);
		}, []);

		const handleSeek = useCallback(e => {
			const vid = videoRef.current;
			const track = trackRef.current;
			if (!vid || !track) return;
			const rect = track.getBoundingClientRect();
			const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
			vid.currentTime = pct * vid.duration;
		}, []);

		const toggleMute = useCallback(() => {
			const vid = videoRef.current;
			if (!vid) return;
			vid.muted = !vid.muted;
			setIsMuted(vid.muted);
		}, []);

		useEffect(() => {
			const vid = videoRef.current;
			if (!vid) return;

			const onEnded = () => setIsPlaying(false);
			const onSeeking = () => setIsBuffering(true);
			const onSeeked = () => setIsBuffering(false);
			const onWaiting = () => setIsBuffering(true);
			const onPlaying = () => setIsBuffering(false);

			vid.addEventListener('timeupdate', handleTimeUpdate);
			vid.addEventListener('loadedmetadata', handleLoadedMetadata);
			vid.addEventListener('ended', onEnded);
			vid.addEventListener('seeking', onSeeking);
			vid.addEventListener('seeked', onSeeked);
			vid.addEventListener('waiting', onWaiting);
			vid.addEventListener('playing', onPlaying);

			// If metadata is already loaded (e.g. src changed and loaded before effect ran)
			if (vid.readyState >= 1) setDuration(vid.duration);

			return () => {
				vid.removeEventListener('timeupdate', handleTimeUpdate);
				vid.removeEventListener('loadedmetadata', handleLoadedMetadata);
				vid.removeEventListener('ended', onEnded);
				vid.removeEventListener('seeking', onSeeking);
				vid.removeEventListener('seeked', onSeeked);
				vid.removeEventListener('waiting', onWaiting);
				vid.removeEventListener('playing', onPlaying);
			};
		}, [videoUrl, handleTimeUpdate, handleLoadedMetadata]);

		// Filter detections visible at current time
		const visibleDetections = detections.filter(
			d => currentTime >= d.start && currentTime <= d.end
		);

		return (
			<Wrapper>
				<VideoFrame>
					{videoUrl ? (
						<video ref={videoCallbackRef} src={videoUrl} preload="metadata" />
					) : (
						<Placeholder />
					)}
					{isBuffering && (
						<SeekingOverlay>
							<Spinner />
						</SeekingOverlay>
					)}
					{visibleDetections.map((det, i) => (
						<DetectionOverlay
							key={i}
							style={{
								top: `${det.y}%`,
								left: `${det.x}%`,
								width: `${det.w}%`,
								height: `${det.h}%`,
							}}>
							{det.label}: {det.count}
						</DetectionOverlay>
					))}
					{virtualLine && (
						<VirtualLineCanvas
							videoElement={videoElement}
							startPoint={virtualLine.start_point}
							endPoint={virtualLine.end_point}
						/>
					)}
				</VideoFrame>
				<Controls>
					<PlayButton onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
						{isPlaying ? <LuPause size={14} /> : <LuPlay size={14} />}
					</PlayButton>
					<Track ref={trackRef} onClick={handleSeek}>
						<TrackFill $pct={progress} />
						<TrackThumb $pct={progress} />
					</Track>
					<TimeLabel>
						{formatTime(currentTime)} / {formatTime(duration)}
					</TimeLabel>
					<VolumeButton
						onClick={toggleMute}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								toggleMute();
							}
						}}
						tabIndex={0}
						role="button"
						aria-label={isMuted ? 'Unmute' : 'Mute'}>
						{isMuted ? <LuVolumeOff size={14} /> : <LuVolume2 size={14} />}
					</VolumeButton>
				</Controls>
			</Wrapper>
		);
	})
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
